/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {IMoney} from "../../cart";
import {ICartRequestHandlerPutCartOptions} from "../../cart/requestHandlers/CartRequestHandler";
import {BaseRequestHandler} from "../../requestHandler/BaseRequestHandler";
import {getRequestConfigurationWithToken} from "../../requestHandler/getRequestConfigWithToken";
import {ICheckoutData} from "../CheckoutData";
import {ICountriesLookup, IStateLookup} from "../Countries/CountriesFactory";
import {IAddressData} from "../IAddressData";
import {IPaymentData} from "../Payment/PaymentData";
import timer from "../../logger/timer";

export class CheckoutRequestHandler {
  public static async authorizePayment(
    paymentTransactionId: string,
    braintreeNonce?: string,
  ): Promise<IPaymentTransaction> {
    const configuration = await getRequestConfigurationWithToken<{braintreeNonce: string} | undefined>();
    if (braintreeNonce !== undefined) {
      configuration.data = {braintreeNonce};
    }

    return BaseRequestHandler.performRequest<IPaymentTransaction, {braintreeNonce: string} | undefined>(
      "POST",
      `/payments/${paymentTransactionId}/authorize`,
      configuration,
    );
  }

  @timer("API", "Checkout")
  public static async countriesLookup(shopId: number): Promise<ICountriesLookup[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<ICountriesLookup[]>(
      "GET",
      `/shop/${shopId}/lookup/countries`,
      configuration,
    );
  }

  @timer("API", "Checkout")
  public static async createCheckout(shopId: number, cartId: string): Promise<ICheckoutData> {
    const configuration = await getRequestConfigurationWithToken<{cartId: string}>();
    configuration.data = {cartId};

    return BaseRequestHandler.performRequest<ICheckoutData, {cartId: string}>(
      "POST",
      `/shop/${shopId}/checkout`,
      configuration,
    );
  }

  @timer("API", "Checkout")
  public static async get(shopId: number, checkoutId: string): Promise<ICheckoutData> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<ICheckoutData>(
      "GET",
      `/shop/${shopId}/checkout/${checkoutId}`,
      configuration,
    );
  }

  @timer("API", "Checkout")
  public static async getBraintreeToken(currencyCode: string): Promise<{clientToken: string}> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<{clientToken: string}>(
      "GET",
      `/payments/braintreeToken/${currencyCode}`,
      configuration,
    );
  }

  /**
   * Get all payment methods
   * @param shopId
   * @param checkoutId
   */
  @timer("API", "Checkout")
  public static async getPayments(shopId: number, checkoutId: string): Promise<IPaymentData[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<IPaymentData[]>(
      "GET",
      `/shop/${shopId}/checkout/${checkoutId}/payment`,
      configuration,
    );
  }

  /**
   * Gets all available shipping options for this checkout
   * @param {number} shopId
   * @param {string} checkoutId
   * @returns {Promise<IGetCheckoutShippingOptionsData[]>}
   */
  @timer("API", "Checkout")
  public static async getShippingOptions(
    shopId: number,
    checkoutId: string,
  ): Promise<IGetCheckoutShippingOptionsData[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<IGetCheckoutShippingOptionsData[]>(
      "GET",
      `/shop/${shopId}/checkout/${checkoutId}/shipping`,
      configuration,
    );
  }

  @timer("API", "Checkout")
  public static async initializePayment(
    shopId: string,
    checkoutId: string,
    amazonPayOrderReferenceId?: string,
  ): Promise<IPaymentTransaction> {
    const configuration = await getRequestConfigurationWithToken<IInitPaymentParams>();
    configuration.data = {
      amazonPayOrderReferenceId,
      checkoutId,
      shopId,
    };

    return BaseRequestHandler.performRequest<IPaymentTransaction, IInitPaymentParams>(
      "POST",
      "/payments",
      configuration,
    );
  }

  /**
   *
   * @param {number} shopId
   * @param {string} checkoutId
   * @param {ICheckoutPutParams} payload
   * @returns {Promise<ICheckoutData>}
   */
  @timer("API", "Checkout")
  public static async putParams(
    shopId: number,
    checkoutId: string,
    payload: ICheckoutPutParams,
  ): Promise<ICheckoutData> {
    const configuration = await getRequestConfigurationWithToken<ICheckoutPutParams>();
    configuration.data = payload;

    return BaseRequestHandler.performRequest<ICheckoutData, ICheckoutPutParams>(
      "PUT",
      `/shop/${shopId}/checkout/${checkoutId}`,
      configuration,
    );
  }

  @timer("API", "Checkout")
  public static async stateLookup(shopId: number, countryCode: string): Promise<IStateLookup[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<IStateLookup[]>(
      "GET",
      `/shop/${shopId}/lookup/countries/${countryCode}/states`,
      configuration,
    );
  }
}

export interface ICheckoutPutParams {
  address?: {
    delivery?: IAddressData;
    invoice?: IAddressData;
  };
  buy?: boolean;
  cart?: ICartRequestHandlerPutCartOptions;
  checkoutId?: string;
  customerRemark?: string;
  favouredDateShipping?: string | null;
  hasNewsletter?: boolean;
  isGuest?: boolean;
  isPartDeliveryAllowed?: boolean;
  logisticsTypeId?: number;
  paymentId?: number | null;
}

// All Checkout options responses

export interface IGetCheckoutShippingOptionsData {
  amount: {
    amount: string;
    currencyCode: string;
    formatted: string;
  };
  deliveryDaysMax: number;
  deliveryDaysMin: number;
  description: string | null;
  extra: string | null;
  favouredDateShipping?: string;
  isBestMatch: boolean;
  isCustomerPreferredShipping?: boolean;
  isExpress: boolean;
  isForwarder: boolean;
  isOversize: boolean;
  isPackingStationAllowed: boolean | null;
  isPartDeliveryAllowed?: boolean;
  isPhoneRequired: boolean;
  isPostOfficeAllowed: boolean | null;
  logisticsTypeId: number;
  name: string;
}

export interface IInitPaymentParams {
  amazonPayOrderReferenceId?: string;
  checkoutId: string;
  shopId: string;
}

export interface IPaymentTransaction {
  capturedAmount: IMoney;
  externalId: string | null;
  paymentTransactionId: string;
  refundedAmount: IMoney;
  state: string;
  totalAmount: IMoney;
}
